/*
 * Entry point of the application.
 * Only platform bootstrapping code should be here.
 * For app-specific initialization, use `app/app.component.ts`.
 */
import { enableProdMode } from '@angular/core';
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';

import { AppModule } from '@app/app.module';
import { environment } from '@env/environment';

if (environment.production) {
  enableProdMode();
}

/**
 * Loads Google Tag Manager using the container ID of the current environment
 * (staging vs production, see environment.ts / environment.prod.ts).
 * Lazy-loaded: fires on the first user interaction, or after a 5s fallback
 * once the page has loaded, so GTM stays off the critical path.
 */
function setupGtm(containerId: string): void {
  if (!containerId) {
    return;
  }
  const w = window as any;
  w.dataLayer = w.dataLayer || [];
  const events = ['scroll', 'click', 'mousemove', 'touchstart'];

  const load = () => {
    if (w.gtmLoaded) {
      return;
    }
    w.gtmLoaded = true;
    events.forEach((e) => window.removeEventListener(e, load));
    w.dataLayer.push({ event: 'gtm.js', 'gtm.start': Date.now() });
    const s = document.createElement('script');
    s.async = true;
    s.src = 'https://www.googletagmanager.com/gtm.js?id=' + encodeURIComponent(containerId);
    document.head.appendChild(s);
  };

  events.forEach((e) => window.addEventListener(e, load, { once: true }));
  if (document.readyState === 'complete') {
    setTimeout(load, 5000);
  } else {
    window.addEventListener('load', () => setTimeout(load, 5000));
  }
}

setupGtm(environment.gtmContainerId);

const bootstrap = () => platformBrowserDynamic().bootstrapModule(AppModule);

if (environment.hmr) {
  import('./hmr').then(({ hmrBootstrap }) => hmrBootstrap(module, bootstrap));
} else {
  bootstrap().catch((err) => console.error(err));
}
